import React, { useState, useEffect, useRef } from 'react';
import TalkToExpert from '../components/TalkToExpert';
import './ServicePage.css';
import './ContactUs.css';

const Careers = () => {
  const [applicant, setApplicant] = useState({
    name: '',
    email: '',
    phone: '',
    subject: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);

  // OTP state
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [triggerMessage, setTriggerMessage] = useState('');
  const [isTriggerDone, setIsTriggerDone] = useState(false);

  // Request tracking (no CSRF/cookies)
  const [requestId, setRequestId] = useState('');
  const [, setIsBootstrapReady] = useState(false);
  const applyFormRef = useRef(null);

  // No cookie access required

  const API_ROOT = 'https://newsite.sysmaco.in:8091'
  const BOOTSTRAP_URL = `${API_ROOT}/api/sendemail/bootstrap`;
  const CAREER_URL = `${API_ROOT}/api/sendemail/career`;
  const REGENERATE_OTP_URL = `${API_ROOT}/api/sendemail/regenerateopt`;
  const TRIGGER_URL = `${API_ROOT}/api/sendemail/trigger`;

  // Bootstrap on mount to receive cookies and request id
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.__SYS_EMAIL_BOOTSTRAP_DONE__) return;
      window.__SYS_EMAIL_BOOTSTRAP_DONE__ = true;
    }
    const init = async () => {
      try {
        const resp = await fetch(BOOTSTRAP_URL, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-store'
        });
        const reqId = resp.headers.get('X-Request-ID') || resp.headers.get('x-request-id') || '';
        if (reqId) setRequestId(reqId);
        if (reqId) setIsBootstrapReady(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Bootstrap call failed:', err);
      }
    };
    init();
  }, [BOOTSTRAP_URL]);

  // Ensure we have X-Request-ID before protected calls
  const ensureBootstrap = async () => {
    if (requestId) return requestId;
    try {
      const resp = await fetch(BOOTSTRAP_URL, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store'
      });
      const reqId = resp.headers.get('X-Request-ID') || resp.headers.get('x-request-id') || '';
      if (reqId) {
        setRequestId(reqId);
        setIsBootstrapReady(true);
      }
      return reqId;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Bootstrap ensure failed:', err);
      return '';
    }
  };

  // Resend OTP countdown
  useEffect(() => {
    if (!isOtpOpen || resendSeconds <= 0) return;
    const timer = setInterval(() => {
      setResendSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [isOtpOpen, resendSeconds]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setApplicant((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (successMsg) setSuccessMsg('');
  };

  const openApplyForm = (positionTitle) => {
    setErrors({});
    setSuccessMsg('');
    setSelectedPositions((prev) => {
      const exists = prev.includes(positionTitle);
      const next = exists ? prev.filter((p) => p !== positionTitle) : [...prev, positionTitle];
      const subjectStr = next.join(', ');
      setApplicant((prevApplicant) => ({ ...prevApplicant, subject: subjectStr }));
      return next;
    });
    if (applyFormRef && applyFormRef.current) {
      applyFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    // Enforce 100 KB size limit immediately
    if (file && file.size > 100 * 1024) {
      setResumeFile(null);
      setErrors((prev) => ({ ...prev, resume: 'Max size 100 KB' }));
      if (e.target) e.target.value = '';
      return;
    }
    setResumeFile(file || null);
    if (errors.resume) setErrors((prev) => ({ ...prev, resume: '' }));
    if (successMsg) setSuccessMsg('');
  };

  const validate = () => {
    const next = {};
    if (!applicant.name.trim()) next.name = 'Name is required';
    if (!applicant.email.trim()) next.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(applicant.email)) next.email = 'Enter a valid email';
    if (!applicant.phone.trim()) next.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(applicant.phone.replace(/\s/g, ''))) next.phone = 'Enter 10 digit phone';
    if (!resumeFile) next.resume = 'Resume is required';
    else if (resumeFile.size > 100 * 1024) next.resume = 'Max size 100 KB';
    else if (!/\.(pdf|doc|docx)$/i.test(resumeFile.name)) next.resume = 'Allowed: PDF, DOC, DOCX';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const rid = await ensureBootstrap();
      if (!rid) {
        setErrors((prev) => ({ ...prev, submit: 'Initialization failed. Please reload and try again.' }));
        return;
      }
      setSubmitting(true);
      setIsSendingOtp(true);
      setSuccessMsg('');
      setOtp('');
      setOtpError('');

      const formData = new FormData();
      formData.append('fullname', applicant.name.trim());
      formData.append('email', applicant.email.trim());
      formData.append('phoneNumber', applicant.phone.trim());
      formData.append('resume', resumeFile);
      if (applicant.subject && applicant.subject.trim()) {
        formData.append('subject', applicant.subject.trim());
      }

      const resp = await fetch(`${CAREER_URL}?emailType=career`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'X-Request-ID': rid
        },
        body: formData
      });
      if (!resp.ok) throw new Error(await resp.text());

      setTriggerMessage('');
      setIsTriggerDone(false);
      setIsOtpOpen(true);
      setIsApplyModalOpen(false);
      setResendSeconds(60);
    } catch (err) {
      setSuccessMsg('');
      setErrors((prev) => ({ ...prev, submit: 'Submission failed. Try again later.' }));
      // eslint-disable-next-line no-console
      console.error('Career apply failed:', err);
    } finally {
      setSubmitting(false);
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendSeconds > 0 || isSendingOtp) return;
    try {
      setIsSendingOtp(true);
      setOtp('');
      setOtpError('');
      const rid = await ensureBootstrap();
      if (!rid) {
        setIsSendingOtp(false);
        setOtpError('Initialization failed. Please reload and try again.');
        return;
      }
      const resp = await fetch(`${REGENERATE_OTP_URL}?emailType=career`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'X-Request-ID': rid
        }
      });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || 'Failed to resend OTP');
      }
      setResendSeconds(60);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Resend OTP failed:', err);
      setOtpError('Could not resend OTP. Try again later.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtpAndSubmit = async () => {
    if (!otp || otp.trim().length !== 6) {
      setOtpError('Enter the 6-digit OTP');
      return;
    }
    setOtpError('');
    try {
      setIsVerifyingOtp(true);
      const rid = await ensureBootstrap();
      if (!rid) {
        setIsVerifyingOtp(false);
        setOtpError('Initialization failed. Please reload and try again.');
        return;
      }
      const triggerResp = await fetch(`${TRIGGER_URL}?emailType=career&otp=${encodeURIComponent(otp.trim())}`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'X-Request-ID': rid
        }
      });
      if (!triggerResp.ok) {
        const errText = await triggerResp.text();
        throw new Error(errText || 'OTP verification failed');
      }

      let message = 'Application submitted successfully.';
      try {
        const data = await triggerResp.json();
        if (data && data.message) message = data.message;
      } catch (_) {}

      setTriggerMessage(message);
      setIsTriggerDone(true);

      // Reset form
      setApplicant({ name: '', email: '', phone: '', subject: '' });
      setSelectedPositions([]);
      setResumeFile(null);
      (document.getElementById('resume') || {}).value = '';
      setResendSeconds(0);
      setOtp('');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Verify/Submit failed:', err);
      setOtpError('OTP verification failed');
    } finally {
      setIsVerifyingOtp(false);
    }
  };
  return (
    <div className="service-page">
      <div className="service-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="service-title">Join Our Team</h1>
            <p className="service-description">
              Build your career with a dynamic IT company that values innovation, 
              growth, and work-life balance.
            </p>
          </div>
        </div>
      </div>

      <div className="service-content">
        <div className="container">
          <div className="content-grid">
            <div className="service-details">
              <h2>Current Openings</h2>
              
              <div className="service-features">
                <div className="feature-item">
                  <div className="feature-header">
                    <div className="feature-icon computer-emoji">&#x1F4BB;&#xFE0E;</div>
                    <h3>Network Engineer</h3>
                  </div>
                  <p>Join our network team to design, implement, and maintain enterprise network infrastructure.</p>
                  <div
                    className="highlight-value-career"
                    onClick={() => openApplyForm('Network Engineer')}
                    role="button"
                    aria-pressed={selectedPositions.includes('Network Engineer')}
                    title={selectedPositions.includes('Network Engineer') ? 'Selected' : 'Select this opportunity'}
                    style={{ cursor: 'pointer', ...(selectedPositions.includes('Network Engineer') ? { backgroundColor: '#0d6efd', color: '#fff' } : {}) }}
                  >
                    {selectedPositions.includes('Network Engineer') ? (
                      <>Opportunities <span aria-hidden="true">✓</span></>
                    ) : (
                      'Opportunities'
                    )}
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-header">
                    <div className="feature-icon">🔒</div>
                    <h3>Cybersecurity Analyst</h3>
                  </div>
                  <p>Protect our clients from digital threats and implement comprehensive security solutions.</p>
                  <div
                    className="highlight-value-career"
                    onClick={() => openApplyForm('Cybersecurity Analyst')}
                    role="button"
                    aria-pressed={selectedPositions.includes('Cybersecurity Analyst')}
                    title={selectedPositions.includes('Cybersecurity Analyst') ? 'Selected' : 'Select this opportunity'}
                    style={{ cursor: 'pointer', ...(selectedPositions.includes('Cybersecurity Analyst') ? { backgroundColor: '#0d6efd', color: '#fff' } : {}) }}
                  >
                    {selectedPositions.includes('Cybersecurity Analyst') ? (
                      <>Opportunities <span aria-hidden="true">✓</span></>
                    ) : (
                      'Opportunities'
                    )}
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-header">
                    <div className="feature-icon">☁️</div>
                    <h3>Cloud Solutions Architect</h3>
                  </div>
                  <p>Design and implement cloud infrastructure solutions for our enterprise clients.</p>
                  <div
                    className="highlight-value-career"
                    onClick={() => openApplyForm('Cloud Solutions Architect')}
                    role="button"
                    aria-pressed={selectedPositions.includes('Cloud Solutions Architect')}
                    title={selectedPositions.includes('Cloud Solutions Architect') ? 'Selected' : 'Select this opportunity'}
                    style={{ cursor: 'pointer', ...(selectedPositions.includes('Cloud Solutions Architect') ? { backgroundColor: '#0d6efd', color: '#fff' } : {}) }}
                  >
                    {selectedPositions.includes('Cloud Solutions Architect') ? (
                      <>Opportunities <span aria-hidden="true">✓</span></>
                    ) : (
                      'Opportunities'
                    )}
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-header">
                    <div className="feature-icon">📞</div>
                    <h3>Technical Support Specialist</h3>
                  </div>
                  <p>Provide exceptional technical support to our clients and resolve complex IT issues.</p>
                  <div
                    className="highlight-value-career"
                    onClick={() => openApplyForm('Technical Support Specialist')}
                    role="button"
                    aria-pressed={selectedPositions.includes('Technical Support Specialist')}
                    title={selectedPositions.includes('Technical Support Specialist') ? 'Selected' : 'Select this opportunity'}
                    style={{ cursor: 'pointer', ...(selectedPositions.includes('Technical Support Specialist') ? { backgroundColor: '#0d6efd', color: '#fff' } : {}) }}
                  >
                    {selectedPositions.includes('Technical Support Specialist') ? (
                      <>Opportunities <span aria-hidden="true">✓</span></>
                    ) : (
                      'Opportunities'
                    )}
                  </div>
                </div>
              </div>

              <div className="benefits-section">
                <h2>Why Work With Us?</h2>
                <ul className="benefits-list">
                  <li>✓ Competitive salary and benefits package</li>
                  <li>✓ Professional development opportunities</li>
                  <li>✓ Flexible work arrangements</li>
                  <li>✓ Latest technology and tools</li>
                  <li>✓ Collaborative team environment</li>
                  <li>✓ Career advancement opportunities</li>
                </ul>
              </div>
            </div>

            <div className="service-sidebar">
              <div className="service-card">
                <h3>Benefits</h3>
                <div className="highlight-item">
                  <span className="highlight-label">Health Insurance:</span>
                  <span className="highlight-value">Covered</span>
                </div>
                <div className="highlight-item">
                  <span className="highlight-label">Vacation Days:</span>
                  <span className="highlight-value">24 Days</span>
                </div>
                <div className="highlight-item">
                  <span className="highlight-label">Industrial Training:</span>
                  <span className="highlight-value">2 Months</span>
                </div>
                <div className="highlight-item">
                  <span className="highlight-label">Work Style:</span>
                  <span className="highlight-value">Hybrid</span>
                </div>
              </div>
            </div>

            <div className="service-details" style={{ marginTop: '2rem', display: 'none' }} ref={applyFormRef} id="apply-now" />
          </div>
        </div>
      </div>

      {/* Apply CTA similar to Get Quote */}
      <div className="service-content">
        <div className="container">
          <div className="content-grid">
            <div className="service-details" style={{ width: '100%' }}>
              {selectedPositions.length > 0 && (
                <div className="benefits-section">
                  <h2>Selected Opportunities ({selectedPositions.length})</h2>
                  <ul className="benefits-list">
                    {selectedPositions.map((pos) => (
                      <li key={pos}>✓ {pos}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={() => {
                    setApplicant((prev) => ({ ...prev, subject: selectedPositions.join(', ') }));
                    setIsApplyModalOpen(true);
                  }}
                  disabled={selectedPositions.length === 0}
                >
                  Apply for Selected{selectedPositions.length > 0 ? ` (${selectedPositions.length})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isApplyModalOpen && (
        <div className="modal-overlay" onClick={() => setIsApplyModalOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="apply-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="apply-title" className="modal-title">Apply Now</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => setIsApplyModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                {errors.submit && <div className="error-message" role="alert">{errors.submit}</div>}
                {successMsg && <div className="success-message" role="status">{successMsg}</div>}

                <div className="form-group">
                  <label htmlFor="subject">Subject (Position Applied For)</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={applicant.subject}
                    onChange={handleChange}
                    readOnly
                    aria-readonly="true"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={applicant.name}
                      onChange={handleChange}
                      className={errors.name ? 'error' : ''}
                      required
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={applicant.email}
                      onChange={handleChange}
                      className={errors.email ? 'error' : ''}
                      required
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={applicant.phone}
                      onChange={handleChange}
                      placeholder="10-digit mobile number"
                      className={errors.phone ? 'error' : ''}
                      required
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="resume">Resume (PDF, DOC, DOCX) *</label>
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFile}
                      className={errors.resume ? 'error' : ''}
                      required
                    />
                    {errors.resume && <span className="error-message">{errors.resume}</span>}
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setIsApplyModalOpen(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary carrer-button-space" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {isOtpOpen && (
        <div className="modal-overlay" onClick={() => setIsOtpOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="otp-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="otp-title" className="modal-title">{isTriggerDone ? 'Success' : 'Enter OTP'}</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => setIsOtpOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {!isTriggerDone ? (
                <>
                  <p>We sent a 6-digit code to {applicant.email}. Enter it below to verify.</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\\d{6}"
                    maxLength="6"
                    className={`opt-input-field otp-input${otpError ? ' error' : ''}`}
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/[^0-9]/g, ''));
                      if (otpError) setOtpError('');
                    }}
                    placeholder="Enter 6-digit OTP"
                    aria-label="One-time password"
                  />
                  {otpError && <div className="error-message" role="alert">{otpError}</div>}

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-secondary opt-btn"
                      onClick={handleResendOtp}
                      disabled={resendSeconds > 0 || isSendingOtp}
                      aria-disabled={resendSeconds > 0 || isSendingOtp}
                      title={resendSeconds > 0 ? `Resend available in ${resendSeconds}s` : 'Resend OTP'}
                    >
                      {isSendingOtp ? 'Sending...' : resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend OTP'}
                    </button>
                    &nbsp;
                    <button
                      type="button"
                      className="btn btn-primary opt-btn"
                      onClick={handleVerifyOtpAndSubmit}
                      disabled={isVerifyingOtp}
                    >
                      {isVerifyingOtp ? 'Verifying...' : 'Verify & Submit'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p>{triggerMessage || 'Application submitted successfully.'}</p>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-primary opt-btn"
                      onClick={() => setIsOtpOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <TalkToExpert />
    </div>
  );
};

export default Careers;
